import { Sentry } from '@config';
import {
  HTTP_401_UNAUTHORIZED,
  HTTP_503_SERVICE_UNAVAILABLE,
} from '@config/const';
import { bringInitialLoginState } from '@reducers/auth';
import { setUnavailable } from '@reducers/system';
import { isRejectedWithValue } from '@reduxjs/toolkit';

export const serviceUnavailableMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    if (isRejectedWithValue(action)) {
      if (
        action.payload.status === HTTP_503_SERVICE_UNAVAILABLE &&
        action.meta?.baseQueryMeta?.response?.headers.get('X-Maintenance')
      ) {
        dispatch(setUnavailable());
      }
    }

    return next(action);
  };

export const unauthenticatedMiddleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    if (isRejectedWithValue(action)) {
      if (action.payload.status === HTTP_401_UNAUTHORIZED) {
        dispatch(bringInitialLoginState());
      }
    }

    return next(action);
  };

class RTKQueryError extends Error {
  constructor(message) {
    super(message);

    this.name = 'RTKQueryError';

    // This line is important for proper stack trace capturing
    // in environments like Node and V8 (Chrome/Edge)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RTKQueryError);
    }
  }
}

export const rtkQueryErrorLogger = () => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    const isFetchBaseQueryError = action.payload && 'status' in action.payload;
    const apiEndpointName = action.meta?.arg?.endpointName;

    let details;

    if (isFetchBaseQueryError) {
      const errorData = action.payload.data;

      details = {
        status: action.payload.status, // HTTP status code
        data: JSON.stringify(errorData, null, 2),
        error: action.payload.error,
      };
    } else {
      details = {
        message: action.error.message || 'Unknown error structure',
      };
    }

    const originalArg = action.meta?.arg;
    const formattedOriginalArg = JSON.stringify(originalArg, null, 2);

    Sentry.captureException(
      isFetchBaseQueryError
        ? new RTKQueryError(
            `RTK Query Error: ${apiEndpointName} - Status ${details.status}`
          )
        : action.error,
      {
        extra: {
          ...details, // Includes status and response body/data
          actionType: action.type,
          endpointName: apiEndpointName,
          originalArgument: formattedOriginalArg, // The arguments passed to the query/mutation
        },
        // Use a fingerprint to group errors based on the API endpoint and status
        fingerprint: [
          'rtk-query-rejected',
          apiEndpointName,
          isFetchBaseQueryError ? String(details.status) : 'client-error',
        ],
        level: 'warning',
      }
    );
  }

  return next(action);
};
